/** Classify an attachment for previewing. Detection prefers the stored MIME type, then falls back to
 *  the filename extension — uploads (and some browsers) frequently send `application/octet-stream` or
 *  `application/zip` for Office files, so the extension is the reliable signal there. */
export type AttachmentKind = 'image' | 'video' | 'html' | 'pdf' | 'docx' | 'xlsx' | 'pptx' | 'other';

export function attachmentKind(a: { content_type?: string | null; filename?: string | null }): AttachmentKind {
  const ct = (a.content_type || '').toLowerCase();
  if (ct.startsWith('image/')) return 'image';
  if (ct.startsWith('video/')) return 'video';
  if (ct.startsWith('text/html') || ct.startsWith('application/xhtml')) return 'html';
  if (ct === 'application/pdf') return 'pdf';
  if (ct.includes('wordprocessingml')) return 'docx';
  if (ct.includes('spreadsheetml')) return 'xlsx';
  if (ct.includes('presentationml')) return 'pptx';
  const ext = (a.filename || '').split('.').pop()?.toLowerCase() || '';
  if (ext === 'pdf') return 'pdf';
  if (ext === 'docx') return 'docx';
  if (ext === 'xlsx') return 'xlsx';
  if (ext === 'pptx') return 'pptx';
  return 'other';
}

/** Kinds that open in the lightbox (pptx shows a "download to view" placeholder — client-side PowerPoint
 *  rendering isn't good enough to ship, but opening the viewer is nicer than a dead row). */
const PREVIEWABLE = new Set<AttachmentKind>(['image', 'video', 'html', 'pdf', 'docx', 'xlsx', 'pptx']);
export const isPreviewable = (a: { content_type?: string | null; filename?: string | null }): boolean =>
  PREVIEWABLE.has(attachmentKind(a));
